/**
 * StaffEditorPage — verbatim port of the prototype Staff editor.
 * Sections: Profile · Account & role · Booking & availability ·
 * Locations & schedule · Services & groups · Skills & notifications.
 */
import { useState, useEffect, useRef, type ReactNode, type DragEvent } from "react";
import { Avatar, Chip, IconBtn, alpha, Btn, Card, Checkbox, Icon, Input, Pill, T, TimeZonePicker, toIanaTimeZone, Toggle } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { LoadState } from "../../shell/composites";
import { TimeZoneNote, zoneLabel } from "../../shell/TimeZoneNote";
import { tzContext } from "../../lib/datetime";
import { isValidEmail, isValidPhone, normalizePhone } from "../../lib/field-validation";
import { staffPhotoUnavailable, subscribeStaffPhoto, subscribeStaffPhotoSession, runEditorPhoto, type EditorPhotoScope, photoUploadActive, photoRemoveAvailable, STAFF_PHOTO_FROM_LINK_COPY, STAFF_PHOTO_UNAVAILABLE_COPY } from "./staff-photo";
import StaffSessions from "./StaffSessions";
import { StaffCalendarConnection } from "../settings/CalendarSyncPanels";
import { useStaffEditorForm } from "./useStaffEditorForm";
import { can, listRoleNames, listMembers, hydrateMembers, subscribeMembers } from "../../shell/access";
import { toast } from "../../shell/toast";
import { WelcomeEmailButton } from "./WelcomeEmailButton";
import { useNav } from "../../shell/router";
import { useLocations } from "../locations/useLocations";
import { useResources, resourceKindLabel } from "../resources/useResources";
import { useServices, svcCategoryName, svcCategoryHue, svcDurationLabel, type Service } from "../services/services-data";
import { ServicePicker } from "../services/ServiceExtras";
import { useStaff, type StaffMember, type StaffGroup } from "./useStaff";
import { staffName, staffInitials } from "./StaffList";
import {
  SVC_CHANNELS, SVC_CHANNEL, WEEKDAYS, type ChannelId,
  SvcSectionHead, SvcRow, SvcSeg, SvcSelect, SvcNumField, SvcTagField,
  ExceptionList, ScheduleGrid, type WeeklyHours,
} from "../../lib/svc-editor-primitives";
import { useT } from "../../lib/i18n";
import { listJourneyBookingLinks, patchBookingLink, isApiError } from "@v0y4ge/api-client";

const STAFF_ROLES = listRoleNames("events");
const DEFAULT_STAFF_ROLE = STAFF_ROLES[STAFF_ROLES.length - 1] ?? "Event Coordinator";
const STAFF_STATUSES: Record<string, { label: string; labelKey: string }> = {
  active: { label: "Active", labelKey: "common.status.active" }, inactive: { label: "Inactive", labelKey: "common.status.inactive" }, "on-leave": { label: "On leave", labelKey: "staff.editorPage.onLeave" },
};

function staffWeek(days?: string[]): WeeklyHours {
  const wk: WeeklyHours = {};
  WEEKDAYS.forEach((d) => { wk[d] = (days ?? ["Mon","Tue","Wed","Thu","Fri"]).includes(d) ? [{ start: "09:00", end: "17:00" }] : []; });
  return wk;
}
function staffLoc(locationId: string, channels: string[]) {
  const schedules: Record<string, WeeklyHours> = {};
  channels.forEach((c) => { schedules[c] = staffWeek(); });
  return { locationId, schedules };
}

function newMember(): StaffMember {
  const _id = "STAFF-" + Date.now();
  return {
    id: _id, staffId: _id, firstName: "", lastName: "", name: "", email: "", phone: "", title: "", role: null,
    bio: "", photo: "", photoUrl: null, status: "active", signsIn: false,
    channels: ["appointments", "events", "queues"] /* every channel by default; untick before saving (CC 0369) */, groups: [], locations: [],
    serviceGrants: [], serviceExclusions: [], skills: [], languages: [],
    department: null, category: null, tags: [],
    concurrency: 1, dailyCap: 0, timeOff: [], slug: "", linkEnabled: false, timezone: null,
    color: "#5142C7", createdAt: new Date().toISOString(), updatedAt: "Just now",
  } satisfies StaffMember;
}

function StaffStatusPill({ status }: { status: string }) {
  const t = useT();
  const map: Record<string, "success" | "neutral" | "warn"> = { active: "success", inactive: "neutral", "on-leave": "warn" };
  const meta = STAFF_STATUSES[status];
  return <Pill tone={map[status] ?? "neutral"} dot>{meta ? t(meta.labelKey, meta.label) : status}</Pill>;
}

/* ── Profile ───────────────────────────────────────────────────────────────────── */
/* A linked member signs in with their ACCOUNT's address, which the team list carries (CC 0374); the
   staff row's own email is a contact address and may be empty. undefined = not linked; "" = linked, but
   the team list has not answered (or does not list the account). */
function useLinkedAccountEmail(userId: string | null | undefined): string | undefined {
  const [, bump] = useState(0);
  useEffect(() => { if (!userId) return; hydrateMembers(); return subscribeMembers(() => bump((n) => n + 1)); }, [userId]);
  if (!userId) return undefined;
  return listMembers().find((m) => m.userId === userId)?.email ?? "";
}

/** The photo control (CC 0465, staff avatars). The photo applies at once through the photo sub-resource, so it is
 *  live only on a stored member and only with staffing.write, in view mode as well as edit mode. */
interface PhotoCtl { busy: boolean; err: string | null; note: string | null; unavailable: boolean; active: boolean; canRemove: boolean; isNew: boolean; onFile: (f: File) => void; onRemove: () => void; onRejectLink: () => void }

/** Staff header: the avatar on the left (it IS the upload control when active), name, badges, title and email on
 *  the right. No photo → initials in the same circle. Read-only → the avatar only. */
function StaffPhotoHeader({ form, photo }: { form: StaffMember; photo: PhotoCtl }) {
  const t = useT();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [over, setOver] = useState(false);
  const [hover, setHover] = useState(false);
  const { busy, err, note, unavailable, active, isNew } = photo;
  const photoUnavailable = unavailable;
  const src = form.photo || form.photoUrl || "";
  const pick = () => { if (active && !busy) inputRef.current?.click(); }; // ui-local
  const onDrop = (e: DragEvent<HTMLDivElement>) => { // ui-local
    e.preventDefault(); setOver(false);
    if (!active || busy) return;
    const f = e.dataTransfer?.files?.[0];
    if (f) photo.onFile(f); else photo.onRejectLink();
  };
  return (
    <div data-testid="staff-photo-header" style={{ display: "flex", alignItems: "flex-start", gap: 24, padding: "20px 22px", borderRadius: 14, border: `1px solid ${T.border}`, background: T.white, marginBottom: 22, flexWrap: "wrap" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <div data-testid="staff-photo" role={active ? "button" : undefined} tabIndex={active ? 0 : undefined}
          aria-label={active ? (src ? t("staff.editorPage.photoReplace", "Replace photo") : t("staff.editorPage.photoAdd", "Add photo")) : undefined}
          onClick={pick} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(); } }}
          onDragOver={(e) => { if (active) { e.preventDefault(); setOver(true); } }} onDragLeave={() => setOver(false)} onDrop={onDrop}
          onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
          style={{ position: "relative", width: 120, height: 120, borderRadius: 999, cursor: active && !busy ? "pointer" : "default", outline: over ? `2px dashed ${T.p1}` : "none", outlineOffset: 3 }}>
          <Avatar size={120} src={src || undefined} initials={staffInitials(form)} />
          {active && (hover || over || busy) ? (
            <div style={{ position: "absolute", inset: 0, borderRadius: 999, background: alpha(T.n1, 62), color: T.white, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", fontSize: 12.5, fontWeight: 700, padding: 14 }}>
              {busy ? t("staff.editorPage.photoUploading", "Uploading photo…") : src ? t("staff.editorPage.photoReplace", "Replace photo") : t("staff.editorPage.photoAdd", "Add photo")}
            </div>
          ) : null}
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/heic" hidden disabled={!active || busy || photoUnavailable}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) photo.onFile(f); }} />
        </div>
        {photo.canRemove && src ? (
          /* ui-raw: text link under the avatar, per the header reference. Shown whenever removal is possible, even
             when upload is unavailable (CC 0469): DELETE never needs the image processor. */
          <button type="button" data-testid="staff-photo-remove" onClick={photo.onRemove} disabled={busy}
            style={{ border: "none", background: "transparent", color: T.p1, fontSize: 13.5, fontWeight: 600, cursor: busy ? "default" : "pointer", fontFamily: T.font, padding: "2px 6px" }}>
            {t("common.actions.remove", "Remove")}
          </button>
        ) : null}
      </div>
      <div style={{ flex: "1 1 240px", minWidth: 0, display: "flex", flexDirection: "column", gap: 8, paddingTop: 6 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: T.n1, overflowWrap: "anywhere" }}>{staffName(form) || t("staff.list.newStaffMember", "New staff member")}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}><StaffStatusPill status={form.status} /></div>
        {form.title ? <div style={{ fontSize: 14, color: T.n2 }}>{form.title}</div> : null}
        {form.email ? <div style={{ fontSize: 14, color: T.n2, overflowWrap: "anywhere" }}>{form.email}</div> : null}
        <div style={{ fontSize: 12, color: T.soft, lineHeight: 1.5, marginTop: 4, display: "flex", flexDirection: "column", gap: 4 }}>
          {/* Suggested keys (not yet in the catalog): staff.editorPage.photoAfterCreate / photoHintAvatar / photoTrust / photoLocationRemoved */}
          {isNew ? <span>{t("staff.editorPage.photoAfterCreate", "You can add a photo once this staff member is created.")}</span>
            : photoUnavailable ? <span>{STAFF_PHOTO_UNAVAILABLE_COPY()}</span>
            : active ? <>
                <span>{t("staff.editorPage.photoHintAvatar", "Click or drop a photo on the avatar. PNG, JPEG, WebP or HEIC, up to 10 MB.")}</span>
                <span>{t("staff.editorPage.photoTrust", "Photos are resized, and embedded data such as location, device and capture time is removed before storage.")}</span>
              </> : null}
          {note ? <span data-testid="staff-photo-note" style={{ color: T.green1, fontWeight: 600 }}>{note}</span> : null}
          {err ? <span data-testid="staff-photo-error" style={{ color: T.red1 }}>{err}</span> : null}
        </div>
      </div>
    </div>
  );
}

function ProfileSection({ form, set, canWrite, photo }: { form: StaffMember; set: (p: Partial<StaffMember>) => void; canWrite: boolean; photo: PhotoCtl }) {
  const accountEmail = useLinkedAccountEmail(form.userId);
  const t = useT();
  const zoneCtx = tzContext();
  /* Contact fields are VALIDATED now (2026-08-07, Pat, operator: "email and
     phone have no verification"). Shape only — no MX/carrier claim — and shown
     on blur so a half-typed address never flashes red mid-keystroke. The save
     path re-checks, so the rule cannot be walked past by never blurring. */
  const [emailErr, setEmailErr] = useState<string | null>(null);
  const [phoneErr, setPhoneErr] = useState<string | null>(null);
  return (
    <div>
      <StaffPhotoHeader form={form} photo={photo} />
      <SvcSectionHead icon={<Icon.user size={18} />} title={t("staff.editorPage.profile", "Profile")} desc={t("staff.editorPage.desc", "Who the staff member is.")} />
      <SvcRow label={t("staff.editorPage.name", "Name")}>
        <div style={{ display: "flex", gap: 10 }}>
          <Input value={form.firstName} onChange={(e) => set({ firstName: e.target.value })} placeholder={t("staff.editorPage.firstName", "First name")} readOnly={!canWrite} />
          <Input value={form.lastName}  onChange={(e) => set({ lastName: e.target.value })}  placeholder={t("staff.editorPage.lastName", "Last name")}  readOnly={!canWrite} />
        </div>
      </SvcRow>
      <SvcRow label={t("staff.editorPage.staffId", "Staff ID")} hint={t("staff.editorPage.hint2", "System identifier.")}>
        <Input value={form.id} readOnly style={{ fontFamily: T.fontMono, maxWidth: 220 }} />
      </SvcRow>
      <SvcRow label={t("staff.editorPage.email", "Email")} hint={
        accountEmail !== undefined
          ? (accountEmail
            ? t("staff.editorPage.signsInWith", "Signs in with {address}. That address belongs to their account; this one is for contact only.", { address: accountEmail })
            : t("staff.editorPage.signsInWithAccount", "Signs in with their linked account. This address is for contact only."))
          : form.signsIn ? t("staff.editorPage.emailHintSignsIn", "Required — this is the address they sign in with.") : undefined}>
        <Input value={form.email} type="email" inputMode="email" autoComplete="email"
          onChange={(e) => { set({ email: e.target.value }); if (emailErr) setEmailErr(null); }}
          onBlur={() => { const v = (form.email ?? "").trim(); setEmailErr(!v || isValidEmail(v) ? null : t("staff.editorPage.emailInvalid", "Enter a valid email address — name@company.com.")); }}
          error={emailErr ?? undefined}
          placeholder={t("staff.editorPage.nameCompanyCom", "name@company.com")} readOnly={!canWrite} testid="staff-email" />
      </SvcRow>
      <SvcRow label={t("staff.editorPage.phone", "Phone")}>
        <Input value={form.phone ?? ""} type="tel" inputMode="tel" autoComplete="tel"
          onChange={(e) => { set({ phone: e.target.value }); if (phoneErr) setPhoneErr(null); }}
          onBlur={() => {
            const v = form.phone ?? "";
            if (!isValidPhone(v)) { setPhoneErr(t("staff.editorPage.phoneInvalid", "Enter a valid phone number — 7–15 digits, optional leading +.")); return; }
            setPhoneErr(null);
            const norm = normalizePhone(v);
            if (norm !== v) set({ phone: norm });   // store one shape, not five
          }}
          error={phoneErr ?? undefined}
          placeholder="+1 (555) 000-0000" readOnly={!canWrite} testid="staff-phone" />
      </SvcRow>
      {/* Default display zone (2026-08-07, Pat) — CanonicalStaffMember.timezone.
          Empty is a real, supported state: the person then follows the workspace
          zone, which is what every record did before this field existed. */}
      <SvcRow label={t("staff.editorPage.timeZone", "Default time zone")} hint={t("staff.editorPage.timeZoneHint", "The zone this person's appointments and schedules are read in. Leave it on the workspace zone for anyone who works where the tenant is based.")}>
        <div style={{ maxWidth: 400 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <TimeZonePicker value={form.timezone ?? ""} disabled={!canWrite} testid="staff-timezone"
                placeholder={t("staff.editorPage.timeZoneEmpty", "Workspace zone")}
                onChange={(v) => set({ timezone: toIanaTimeZone(v) || null } as Partial<StaffMember>)} />
            </div>
            {canWrite && form.timezone ? (
              <Btn variant="ghost" size="sm" onClick={() => set({ timezone: null } as Partial<StaffMember>)}>{t("staff.editorPage.timeZoneClear", "Use workspace zone")}</Btn>
            ) : null}
          </div>
          <TimeZoneNote tone="panel" testid="staff-timezone-note">
            {form.timezone ? (<>
              {t("staff.editorPage.timeZoneSet", "Appointments for this person are shown in")} <b style={{ color: T.n2 }}>{zoneLabel(form.timezone)}</b>
              {form.timezone !== zoneCtx.zone ? <>{" — "}{t("staff.editorPage.timeZoneYours", "your own view is")} {zoneLabel(zoneCtx.zone)}</> : null}.
            </>) : (<>
              {t("staff.editorPage.timeZoneUnset", "No personal zone — times follow the workspace zone")} <b style={{ color: T.n2 }}>{zoneLabel(zoneCtx.zone)}</b>.
            </>)}
          </TimeZoneNote>
        </div>
      </SvcRow>
      <SvcRow label={t("staff.editorPage.jobTitle", "Job title")} hint={t("staff.editorPage.hint3", "Shown on the booking page if bookable by name.")}>
        <Input value={form.title ?? ""} onChange={(e) => set({ title: e.target.value })} placeholder={t("staff.editorPage.eGSeniorPersonalBanker", "e.g. Senior personal banker")} readOnly={!canWrite} />
      </SvcRow>
      <SvcRow label={t("staff.editorPage.publicBio", "Public bio")} hint={t("staff.editorPage.hint4", "Optional blurb for the public profile.")} full>
        <textarea value={form.bio ?? ""} onChange={(e) => set({ bio: e.target.value })} disabled={!canWrite} rows={3} placeholder={t("staff.editorPage.aShortIntroductionCustomersSeeWhenChoosingThisPerson", "A short introduction customers see when choosing this person.")} style={{ width: "100%", border: `1px solid ${T.n4}`, borderRadius: 8, padding: "10px 12px", fontSize: 14, fontFamily: T.font, color: T.n1, resize: "vertical", background: canWrite ? T.white : T.n6 }} />
      </SvcRow>
    </div>
  );
}

/* ── Account & role ──────────────────────────────────────────────────────── */
function AccountSection({ form, set, canWrite, canManageSessions, isNew }: { form: StaffMember; set: (p: Partial<StaffMember>) => void; canWrite: boolean; canManageSessions: boolean; isNew: boolean }) {
  const t = useT();
  return (
    <div>
      <SvcSectionHead icon={<Icon.lock size={18} />} title={t("staff.editorPage.accountRole", "Account & role")} desc={t("staff.editorPage.desc2", "A staff member is the same object as a user — sign-in is optional.")} />
      <SvcRow label={t("staff.editorPage.canSignIn", "Can sign in?")} hint={t("staff.editorPage.hint5", "Bookable staff who never sign in can stay account-less.")}>
        <Toggle on={!!form.signsIn} label={form.signsIn ? t("staff.staffEditorPage.hasALogin", "Has a login") : t("staff.list.noSignIn", "No sign-in")} onChange={canWrite ? (v) => set({ signsIn: v, role: v ? (form.role ?? DEFAULT_STAFF_ROLE) : null }) : undefined} />
      </SvcRow>
      {form.signsIn ? (
        <SvcRow label={t("staff.editorPage.studioRole", "Studio role")} hint={t("staff.editorPage.hint6", "Determines product permissions when they sign in.")}>
          <div style={{ maxWidth: 280 }}><SvcSelect value={form.role ?? DEFAULT_STAFF_ROLE} onChange={(v) => set({ role: v })} disabled={!canWrite} options={STAFF_ROLES.map((r) => ({ id: r, label: r }))} /></div>
        </SvcRow>
      ) : (
        <div style={{ marginTop: 14, display: "flex", gap: 11, padding: "13px 15px", borderRadius: 11, background: T.n6, border: `1px solid ${T.border}` }}>
          <span style={{ color: T.soft, flexShrink: 0 }}><Icon.info size={16} /></span>
          <p style={{ margin: 0, fontSize: 13, color: T.n2 }}>{t("staff.editorPage.thisPersonHasNoLoginAndWonTAppearInTeamRoles", "This person has no login and won't appear in Team & Roles. They're still fully bookable — set their channels under")}{" "}<b>{t("staff.editorPage.bookingAvailability", "Booking & availability")}</b>.</p>
        </div>
      )}
      {/* Active sessions + the account-takeover response (2026-08-07, Pat).
          A member that has not been created yet has no sessions to read — the
          op would 404 on an id the server has never seen. */}
      {isNew ? null : (
        <StaffSessions staffId={form.id} staffName={[form.firstName, form.lastName].filter(Boolean).join(" ")}
          canWrite={canManageSessions} signsIn={!!form.signsIn}
          onStatusChange={(status) => set({ status })} />
      )}
    </div>
  );
}

/* ── Booking & availability ────────────────────────────────────────────── */
/* ── Personal booking link (CC 0303, JAI-2546) ──────────────────────────────
   Reads listJourneyBookingLinks for this member's row; the switch is one patchBookingLink { enabled }.
   The address is the row's publicUrl as the server sends it (CC 0308), never composed here: null = no
   appointment journey is published; absent (an older server) = "Address not available".
   A synced member gets the same row. The row isn't shown on a new member (BookingSection gates it). */
function PersonalBookingLinkRow({ staffId }: { staffId: string }) {
  const t = useT();
  const canSwitch = can("journeys.write");
  const [st, setSt] = useState<{ phase: "loading" | "ready" | "missing" | "error"; url?: string; slug?: string; on?: boolean; noJourney?: boolean; locCount?: number }>({ phase: "loading" });
  const [busy, setBusy] = useState(false);
  const load = () => {
    let live = true;
    setSt({ phase: "loading" });
    listJourneyBookingLinks()
      .then((d) => {
        if (!live) return;
        const me = d.staff.find((s) => s.id === staffId);
        if (!me || !me.slug) { setSt({ phase: "missing" }); return; }
        /* The address the server gives (CC 0308 §1); the screen never builds one. null = no appointment
           journey is published, so there is nothing to share. */
        setSt({ phase: "ready", slug: me.slug, on: me.linkEnabled, url: me.publicUrl ?? undefined, noJourney: me.publicUrl === null, locCount: me.bookableLocationCount });
      })
      .catch((err) => { if (live) { console.warn("booking links read failed:", err); setSt({ phase: "error" }); } });
    return () => { live = false; };
  };
  useEffect(load, [staffId]);
  const flip = async (next: boolean) => {
    setBusy(true);
    try {
      const row = await patchBookingLink(staffId, { enabled: next });
      setSt((s) => ({ ...s, on: row.linkEnabled, ...(row.publicUrl !== undefined ? { url: row.publicUrl ?? undefined, noJourney: row.publicUrl === null } : {}), ...(row.bookableLocationCount !== undefined ? { locCount: row.bookableLocationCount } : {}) }));
      toast({ tone: "success", title: row.linkEnabled ? t("staff.editorPage.bookingLinkOn", "Booking link switched on") : t("staff.editorPage.bookingLinkOff", "Booking link switched off") });
    } catch (err) {
      console.warn("booking link switch failed:", err);
      const notFound = isApiError(err) && err.code === "STAFF_NOT_FOUND";
      toast({ tone: "danger", title: t("staff.editorPage.bookingLinkFailed", "Couldn't change the booking link"), subtitle: notFound ? t("staff.editorPage.bookingLinkNotFound", "This staff member no longer exists. Reload the page.") : t("staff.list.retryInAMoment", "Retry in a moment.") });
    } finally { setBusy(false); }
  };
  const copy = () => {
    if (!st.url) return;
    navigator.clipboard?.writeText(st.url).then(
      () => toast({ tone: "success", title: t("staff.editorPage.bookingLinkCopied", "Link copied") }),
      () => toast({ tone: "danger", title: t("staff.editorPage.bookingLinkCopyFailed", "Couldn't copy the link"), subtitle: st.url }),
    );
  };
  return (
    <SvcRow label={t("staff.editorPage.personalBookingLink", "Personal booking link")} hint={t("staff.editorPage.personalBookingLinkHint", "Opens the standard booking journey, limited to this person. Also visible in the staff directory on the public site when on.")}>
      {st.phase === "loading" ? (
        <div style={{ fontSize: 13, color: T.soft }}>{t("staff.editorPage.bookingLinkLoading", "Loading the booking link…")}</div>
      ) : st.phase === "error" ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: T.n2 }}>{t("staff.editorPage.bookingLinkLoadFailed", "Couldn't load the booking link.")}</span>
          <Btn size="sm" variant="secondary" onClick={() => { load(); }}>{t("common.actions.tryAgain", "Try again")}</Btn>
        </div>
      ) : st.phase === "missing" ? (
        <div style={{ fontSize: 13, color: T.n2 }}>{t("staff.editorPage.bookingLinkMissing", "This person has no booking link yet.")}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {st.noJourney ? (
            <div data-testid="staff-booking-link-no-journey" style={{ fontSize: 13, color: T.n2 }}>{t("staff.editorPage.bookingLinkNoJourney", "No appointment journey is published yet, so there is no link to share.")}</div>
          ) : null}
          {/* An address that leads nowhere (CC 0330): no store has this person on its roster. Absent count = shown nothing. */}
          {st.locCount === 0 ? (
            <div role="status" data-testid="staff-booking-link-no-location" style={{ fontSize: 12.5, lineHeight: 1.45, color: "#B54708", background: "#FFFAEB", border: "1px solid #FEDF89", borderRadius: 8, padding: "8px 11px" }}>
              {t("staff.editorPage.bookingLinkNoLocation", "No location can offer this person, so the link leads nowhere. Add them to a location's roster before you share it.")}
            </div>
          ) : null}
          {st.noJourney ? null : <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <code data-testid="staff-booking-link-url" style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12.5, padding: "8px 11px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.n6, color: st.on ? T.n1 : T.soft }}>{st.url ?? t("staff.editorPage.bookingLinkNoAddress", "Address not available")}</code>
            <Btn size="sm" variant="secondary" icon={<Icon.copy size={13} />} onClick={copy} disabled={!st.url}>{t("common.actions.copy", "Copy")}</Btn>
          </div>}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <span title={canSwitch ? undefined : t("staff.editorPage.bookingLinkNoPermission", "You need permission to edit journeys to switch this.")}>
              <Toggle on={!!st.on} onChange={canSwitch && !busy ? (v) => { void flip(v); } : undefined} />
            </span>
            <span data-testid="staff-booking-link-state" style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.45 }}>
              {st.on ? t("staff.editorPage.bookingLinkOnHint", "Customers can book this person at the link") : t("staff.editorPage.bookingLinkOffHint", "The link is switched off; the page answers that this person is not taking bookings")}
            </span>
          </div>
        </div>
      )}
    </SvcRow>
  );
}

function BookingSection({ form, set, canWrite, isNew }: { form: StaffMember; set: (p: Partial<StaffMember>) => void; canWrite: boolean; isNew: boolean }) {
  const t = useT();
  const chan = (id: ChannelId) => (form.channels ?? []).includes(id);
  const toggleChan = (id: ChannelId) => set({ channels: chan(id) ? (form.channels ?? []).filter((c) => c !== id) : [...(form.channels ?? []), id] });
  return (
    <div>
      <SvcSectionHead icon={<Icon.calendar size={18} />} title={t("staff.editorPage.bookingAvailability", "Booking & availability")} desc={t("staff.editorPage.desc3", "Where they're bookable and how much.")} />
      <SvcRow label={t("staff.editorPage.status", "Status")} hint={t("staff.editorPage.hint7", "Inactive / on-leave staff drop out of the booking pool.")}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ minWidth: 180 }}><SvcSelect value={form.status} onChange={(v) => set({ status: v as StaffMember["status"] })} disabled={!canWrite} options={Object.entries(STAFF_STATUSES).map(([id, v]) => ({ id, label: t(v.labelKey, v.label) }))} /></div>
        </div>
      </SvcRow>
      <SvcRow label={t("staff.editorPage.bookableChannels", "Bookable channels")} hint={t("staff.editorPage.hint8", "Authorize any combination. Leaving all off removes them from the booking pool.")}>
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {SVC_CHANNELS.map((c) => (
            <label key={c.id} onClick={canWrite ? () => toggleChan(c.id) : undefined} style={{ display: "flex", alignItems: "center", gap: 11, padding: "10px 13px", borderRadius: 10, border: `1px solid ${chan(c.id) ? T.p2 : T.border}`, background: chan(c.id) ? T.p4 : T.white, cursor: canWrite ? "pointer" : "default" }}>
              <Checkbox checked={chan(c.id)} />
              <span style={{ width: 28, height: 28, borderRadius: 7, background: alpha(c.hue, "1f"), color: c.hue, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                {(Icon as Record<string, (p: {size:number}) => ReactNode>)[c.icon]?.({ size: 15 })}
              </span>
              <span style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{c.label}</span>
            </label>
          ))}
        </div>
      </SvcRow>
      <SvcRow label={t("staff.editorPage.bookingMode", "Booking mode")} hint={t("staff.editorPage.hint9", "Whether customers can request this person by name, or are pooled into next-available.")}>
        <SvcSeg value={(form as any).byName ? "name" : "any"} onChange={(v) => set({ ...(v === "name" ? { byName: true } : { byName: false }) } as any)} disabled={!canWrite} options={[{ id: "name", label: t("staff.editorPage.bookableByName", "Bookable by name") }, { id: "any", label: t("staff.editorPage.anyAvailablePool", "Any-available pool") }]} />
      </SvcRow>
      {isNew || !form.id ? null : <PersonalBookingLinkRow staffId={String(form.id)} />}
      <SvcRow label={t("staff.editorPage.concurrency", "Concurrency")} hint={t("staff.editorPage.hint10", "How many bookings this person can run at once.")}>
        <SvcNumField value={form.concurrency ?? 1} onChange={(v) => set({ concurrency: v })} min={1} max={50} unit="at once" width={140} disabled={!canWrite} />
      </SvcRow>
      <SvcRow label={t("staff.editorPage.dailyBookingCap", "Daily booking cap")} hint={t("staff.editorPage.hint11", "Maximum bookings per day. Leave 0 = no cap.")}>
        <SvcNumField value={form.dailyCap ?? 0} onChange={(v) => set({ dailyCap: v })} min={0} max={200} unit={(form.dailyCap == null || form.dailyCap === 0) ? "· no cap" : "per day"} width={160} disabled={!canWrite} />
      </SvcRow>
      <SvcRow label={t("staff.editorPage.timeOffExceptions", "Time off & exceptions")} hint={t("staff.editorPage.hint12", "One-off dates that block availability.")} full>
        <ExceptionList items={form.timeOff ?? []} onChange={(next) => set({ timeOff: next } as any)} canWrite={canWrite} />
      </SvcRow>
    </div>
  );
}

/* ── Locations & schedule ────────────────────────────────────────────────── */
function ScheduleSection({ form, set, canWrite }: { form: StaffMember; set: (p: Partial<StaffMember>) => void; canWrite: boolean }) {
  const t = useT();
  const channels = form.channels ?? [];
  const assigned: { locationId: string; schedules: Record<string, WeeklyHours> }[] = (form.locations ?? []) as any;
  const [activeLoc, setActiveLoc] = useState<string | null>(assigned[0]?.locationId ?? null);
  const [activeChan, setActiveChan] = useState<string | null>(channels[0] ?? null);

  useEffect(() => { if (!assigned.find((a) => a.locationId === activeLoc)) setActiveLoc(assigned[0]?.locationId ?? null); }, [assigned.map((a) => a.locationId).join(",")]);
  useEffect(() => { if (!channels.some((c) => c === activeChan)) setActiveChan(channels[0] ?? null); }, [channels.join(",")]);

  // Real locations (same source as the Locations list/editor).
  const { locations, loading: locsLoading } = useLocations();
  const available = locations.filter((l) => !assigned.find((a) => a.locationId === l.id));
  const addLoc = (locId: string) => { set({ locations: [...assigned, staffLoc(locId, channels.length ? channels : ["appointments"])] } as any); setActiveLoc(locId); };
  const rmLoc = (locId: string) => set({ locations: assigned.filter((a) => a.locationId !== locId) } as any);
  const cur = assigned.find((a) => a.locationId === activeLoc);
  const locName = (id: string) => locations.find((l) => l.id === id)?.name ?? id;
  const setGrid = (wk: WeeklyHours) => { if (!cur || !activeChan) return; set({ locations: assigned.map((a) => a.locationId === activeLoc ? { ...a, schedules: { ...a.schedules, [activeChan]: wk } } : a) } as any); };
  /* `schedules` is optional (CC 0299): absent for a member at a location with no hours. */
  const grid = cur && activeChan ? (cur.schedules?.[activeChan] ?? staffWeek([])) : null;

  return (
    <div>
      <SvcSectionHead icon={<Icon.loc size={18} />} title={t("staff.editorPage.locationsSchedule", "Locations & schedule")} desc={t("staff.editorPage.desc4", "Assign locations; set a schedule per location and per channel.")} />
      {channels.length === 0 ? (
        <div style={{ marginTop: 16, padding: "16px", borderRadius: 11, background: "#FFFAED", border: "1px solid #F2E6C7", fontSize: 13, color: T.n2 }}>{t("staff.editorPage.enableAtLeastOneBookableChannelUnder", "Enable at least one bookable channel under")}{" "}<b>{t("staff.editorPage.bookingAvailability", "Booking & availability")}</b> {t("staff.editorPage.toSetASchedule", "to set a schedule.")}</div>
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "16px 0", borderTop: `1px solid ${T.border}`, alignItems: "center" }}>
            {assigned.map((a) => { const on = activeLoc === a.locationId; return (
              <span key={a.locationId} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 8px 7px 13px", borderRadius: 999, border: `1px solid ${on ? T.p1 : T.n4}`, background: on ? T.p4 : T.white, cursor: "pointer" }} onClick={() => setActiveLoc(a.locationId)}>
                <Icon.loc size={13} /><span style={{ fontSize: 13, fontWeight: 600, color: on ? T.p1 : T.n2 }}>{locName(a.locationId)}</span>
                {canWrite ? <IconBtn size={32} onClick={(e) => { e.stopPropagation(); rmLoc(a.locationId); }}><Icon.x size={12} /></IconBtn> : null}
              </span>
            ); })}
            {canWrite && available.length ? (
              <select value="" onChange={(e) => e.target.value && addLoc(e.target.value)} style={{ height: 34, padding: "0 10px", border: `1px dashed ${T.borderStrong}`, borderRadius: 999, background: T.white, color: T.p1, fontSize: 13, fontWeight: 600, fontFamily: T.font, cursor: "pointer" }}>
                <option value="">{t("staff.editorPage.addLocation", "+ Add location")}</option>
                {available.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            ) : canWrite && locsLoading ? (
              <span style={{ fontSize: 13, color: T.soft }}>{t("staff.editorPage.loadingLocations", "Loading locations…")}</span>
            ) : null}
          </div>
          {!cur ? <div style={{ padding: "30px 16px", textAlign: "center", color: T.soft, border: "1.5px dashed #E5E5E5", borderRadius: 12 }}>{t("staff.editorPage.noLocationsAssignedAddOneToSetASchedule", "No locations assigned. Add one to set a schedule.")}</div> : (
            <div>
              <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${T.border}`, marginBottom: 8 }}>
                {channels.map((id) => { const c = SVC_CHANNEL(id); const on = activeChan === id; return (
                  /* ui-raw: channel tab strip (2px underline on active) — Tabs' pattern (group 8) */
                  <button key={id} onClick={() => setActiveChan(id)} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 14px", border: "none", borderBottom: `2px solid ${on ? T.p1 : "transparent"}`, background: "transparent", color: on ? T.p1 : T.n2, fontWeight: on ? 700 : 500, fontSize: 13.5, cursor: "pointer", fontFamily: T.font }}>
                    {(Icon as Record<string, (p:{size:number}) => ReactNode>)[c.icon]?.({ size: 15 })} {c.label}
                  </button>
                ); })}
              </div>
              {grid ? <ScheduleGrid key={(activeLoc ?? "") + ":" + (activeChan ?? "")} schedule={grid} onChange={setGrid} canWrite={canWrite} bulk /> : null}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Services & groups ───────────────────────────────────────────────────── */
function ServicesSection({ form, set, canWrite, groups }: { form: StaffMember; set: (p: Partial<StaffMember>) => void; canWrite: boolean; groups: StaffGroup[] }) {
  const t = useT();
  const svcStore = useServices();
  const { items: allResources } = useResources();
  const [pickSvc, setPickSvc] = useState(false);
  const [pickRes, setPickRes] = useState(false);

  const inGroup = (gid: string) => (form.groups ?? []).includes(gid);
  const toggleGroup = (gid: string) => set({ groups: inGroup(gid) ? (form.groups ?? []).filter((g) => g !== gid) : [...(form.groups ?? []), gid] });

  // Individual grants live on the CONTRACT field `serviceGrants` (CanonicalStaffMember);
  // resource authorizations are the additive `resourceIds` (declared for the staff DTO).
  const serviceIds = form.serviceGrants ?? [];
  const resourceIds = (form as { resourceIds?: string[] }).resourceIds ?? [];
  const grantedServices = svcStore.services().filter((s: Service) => serviceIds.includes(s.id));
  const grantedResources = allResources.filter((r) => resourceIds.includes(r.id));

  const addService = (s: Service) => set({ serviceGrants: [...new Set([...serviceIds, s.id])] });
  const rmService = (id: string) => set({ serviceGrants: serviceIds.filter((x) => x !== id) });
  const addResource = (r: { id: string }) => set({ resourceIds: [...new Set([...resourceIds, r.id])] } as Partial<StaffMember>);
  const rmResource = (id: string) => set({ resourceIds: resourceIds.filter((x) => x !== id) } as Partial<StaffMember>);

  const emptyBox = (msg: string) => <div style={{ padding: "20px 16px", textAlign: "center", color: T.soft, fontSize: 13, border: `1.5px dashed ${T.border}`, borderRadius: 10 }}>{msg}</div>;
  const rowStyle = { display: "flex", alignItems: "center", gap: 11, padding: "10px 12px", border: `1px solid ${T.border}`, borderRadius: 10, background: T.white } as const;
  const rmBtn = (onClick: () => void) => canWrite ? <IconBtn size={32} onClick={onClick} title={t("common.actions.remove", "Remove")}><Icon.x size={14} /></IconBtn> : null;

  return (
    <div>
      <SvcSectionHead icon={<Icon.layers size={18} />} title={t("staff.editorPage.servicesGroups", "Services & groups")} desc={t("staff.editorPage.desc5", "What this person can deliver — from their groups, plus individually granted services — and the resources they're authorized to use.")} />
      <SvcRow label={t("staff.editorPage.groupMembership", "Group membership")} hint={t("staff.editorPage.hint13", "Members inherit every service their groups support.")}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {groups.length === 0 ? <div style={{ fontSize: 13, color: T.soft }}>{t("staff.editorPage.noGroupsYet", "No groups yet.")}</div> : groups.map((g) => { const on = inGroup(g.id); const hue = g.hue ?? T.p1; return (
            <Chip key={g.id} selected={on} disabled={!canWrite} onClick={() => toggleGroup(g.id)}
              leading={<span style={{ width: 9, height: 9, borderRadius: 3, background: hue, display: "inline-block" }} />}>{g.name}</Chip>
          ); })}
        </div>
      </SvcRow>

      <SvcRow label={t("staff.editorPage.individualServices", "Individual services")} hint={t("staff.editorPage.hint14", "Grant specific services on top of the groups above — for services this person can deliver that their groups don't cover.")} full>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {grantedServices.length === 0 ? emptyBox(canWrite ? "No individually-granted services. Add one to authorize it directly." : "No individually-granted services.") : grantedServices.map((s: Service) => (
            <div key={s.id} style={rowStyle}>
              <span style={{ width: 30, height: 30, borderRadius: 7, background: alpha(svcCategoryHue(s.category), "1f"), color: svcCategoryHue(s.category), display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.layers size={15} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</div>
                <div style={{ fontSize: 11.5, color: T.soft }}>{svcCategoryName(s.category)}{s.duration ? t("staff.editorPage.text7", "· {arg1}", { arg1: svcDurationLabel(s.duration) }) : ""}</div>
              </div>
              {s.status !== "published" ? <Pill tone="warn">{t("common.status.draft", "Draft")}</Pill> : null}
              {rmBtn(() => rmService(s.id))}
            </div>
          ))}
          {canWrite ? <div><Btn variant="secondary" size="sm" icon={<Icon.plus size={13} />} onClick={() => setPickSvc(true)}>{t("staff.editorPage.addService", "Add service")}</Btn></div> : null}
        </div>
      </SvcRow>

      <SvcRow label={t("staff.editorPage.resources", "Resources")} hint={t("staff.editorPage.hint15", "Authorize the equipment, rooms and devices this person is allowed to use when delivering services.")} full>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {grantedResources.length === 0 ? emptyBox(canWrite ? "No resources authorized. Add rooms, equipment or devices this person can use." : "No resources authorized.") : grantedResources.map((r) => (
            <div key={r.id} style={rowStyle}>
              <span style={{ width: 30, height: 30, borderRadius: 7, background: alpha(r.color || T.p1, "1f"), color: r.color || T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.grid size={15} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</div>
                <div style={{ fontSize: 11.5, color: T.soft }}>{resourceKindLabel(r.kind, t)}{r.type ? t("staff.editorPage.text8", "· {type}", { type: r.type }) : ""}</div>
              </div>
              {rmBtn(() => rmResource(r.id))}
            </div>
          ))}
          {canWrite ? <div><Btn variant="secondary" size="sm" icon={<Icon.plus size={13} />} onClick={() => setPickRes(true)}>{t("staff.editorPage.addResource", "Add resource")}</Btn></div> : null}
        </div>
      </SvcRow>

      {pickSvc ? <ServicePicker title={t("staff.editorPage.grantAService", "Grant a service")} exclude={serviceIds} onPick={addService} onClose={() => setPickSvc(false)} /> : null}
      {pickRes ? <ResourcePicker title={t("staff.editorPage.authorizeAResource", "Authorize a resource")} exclude={resourceIds} onPick={addResource} onClose={() => setPickRes(false)} /> : null}
    </div>
  );
}

/* ── Resource picker (modal) — mirrors ServicePicker's browse-and-add UX ──── */
function ResourcePicker({ onPick, onClose, exclude = [], title = "Add a resource" }: {
  onPick: (r: { id: string; name: string; kind: string; type: string; color: string }) => void; onClose: () => void; exclude?: string[]; title?: string;
}) {
  const t = useT();
  const { items } = useResources();
  const [query, setQuery] = useState("");
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); }; document.addEventListener("keydown", k); return () => document.removeEventListener("keydown", k); }, [onClose]);
  const ex = new Set(exclude);
  const list = items.filter((r) => !ex.has(r.id) && (!query.trim() || `${r.name} ${resourceKindLabel(r.kind, t)} ${r.type}`.toLowerCase().includes(query.toLowerCase())));
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 240, background: "rgba(20,16,46,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title} style={{ width: 560, maxWidth: "94vw", maxHeight: "86vh", background: T.white, borderRadius: 14, boxShadow: T.shadowLg, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: T.n1, margin: 0 }}>{title}</h2>
          <IconBtn size={32} onClick={onClose} aria-label={t("common.actions.close", "Close")}><Icon.x size={15} /></IconBtn>
        </div>
        <div style={{ padding: "12px 16px 8px" }}>
          <Input full leading={<Icon.search size={14} />} size="sm" autoFocus placeholder={t("staff.editorPage.searchResources", "Search resources…")} value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
          {list.length === 0 ? <div style={{ padding: 24, textAlign: "center", color: T.soft, fontSize: 13 }}>{query.trim() ? t("staff.editorPage.text3", "No resources match.") : t("staff.editorPage.text4", "No resources to add.")}</div> :
            list.map((r) => (
              /* ui-raw: full-width clickable list/picker row — no row-as-button component exists */
              <button key={r.id} onClick={() => { onPick(r); onClose(); }} style={{ display: "flex", alignItems: "center", gap: 12, padding: 11, border: `1px solid ${T.border}`, borderRadius: 10, background: T.white, cursor: "pointer", fontFamily: T.font, textAlign: "left" }}>
                <span style={{ width: 32, height: 32, borderRadius: 7, background: alpha(r.color || T.p1, "1f"), color: r.color || T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.grid size={15} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{r.name}</div>
                  <div style={{ fontSize: 12, color: T.soft }}>{resourceKindLabel(r.kind, t)}{r.type ? ` · ${r.type}` : ""}</div>
                </div>
                <Icon.plus size={16} />
              </button>
            ))}
        </div>
      </div>
    </div>
  );
}

/* ── Skills & notifications ────────────────────────────────────────────── */
function SkillsSection({ form, set, canWrite, persisted, calendarRead, calendarWrite }: { form: StaffMember; set: (p: Partial<StaffMember>) => void; canWrite: boolean; persisted: boolean; calendarRead: boolean; calendarWrite: boolean }) {
  const t = useT();
  const notify = (form as any).notify ?? {};
  const setNotify = (patch: object) => set({ notify: { ...notify, ...patch } } as any);
  return (
    <div>
      <SvcSectionHead icon={<Icon.tags size={18} />} title={t("staff.editorPage.skillsNotifications", "Skills & notifications")} desc={t("staff.editorPage.desc6", "Matching attributes and how they're kept in the loop.")} />
      <SvcRow label={t("staff.editorPage.skills", "Skills")} hint={t("staff.editorPage.hint16", "Drives matching and filtering (e.g. 'book someone who does bridal').")}>
        <SvcTagField tags={(form as any).skills ?? []} onChange={(v) => set({ skills: v } as any)} disabled={!canWrite} placeholder={t("staff.editorPage.addASkill", "Add a skill…")} />
      </SvcRow>
      <SvcRow label={t("staff.editorPage.languages", "Languages")} hint={t("staff.editorPage.hint17", "Spoken languages, for 'book someone who speaks Spanish'.")}>
        <SvcTagField tags={(form as any).languages ?? []} onChange={(v) => set({ languages: v } as any)} disabled={!canWrite} placeholder={t("staff.editorPage.addALanguage", "Add a language…")} />
      </SvcRow>
      <SvcRow label={t("staff.editorPage.notifications", "Notifications")} hint={t("staff.editorPage.hint18", "When this staff member is told about bookings.")}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {([["newBooking","New bookings"], ["changes","Changes & cancellations"], ["reminders","Upcoming reminders"]] as [string,string][]).map(([k, lbl]) => (
            <label key={k} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Toggle on={!!notify[k]} onChange={canWrite ? (v) => setNotify({ [k]: v }) : undefined} />
              <span style={{ fontSize: 13.5, color: T.n2 }}>{lbl}</span>
            </label>
          ))}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 2 }}>
            <span style={{ fontSize: 13.5, color: T.n2 }}>{t("staff.editorPage.via", "Via")}</span>
            <div style={{ width: 140 }}><SvcSelect value={notify.channel ?? "email"} onChange={(v) => setNotify({ channel: v })} disabled={!canWrite} options={[{ id: "email", label: t("staff.editorPage.email", "Email") }, { id: "sms", label: t("staff.editorPage.sms", "SMS") }, { id: "both", label: t("staff.editorPage.emailSms", "Email + SMS") }]} /></div>
          </div>
        </div>
      </SvcRow>
      {/* The "Calendar sync" row was REMOVED here (2026-09-18, CC 0001-calendar).

          It wrote `form.calendarSync` — a field that is not on the staff
          contract, is not in the save payload and is read by nothing. The
          toggle flipped, the editor looked changed, Save succeeded, and no
          calendar was ever connected; it also offered Outlook and iCal, which
          this product's calendar flow (a tenant-level Google Workspace service
          account with domain-wide delegation) does not support at all.

          Deliberately left EMPTY rather than replaced with a disabled
          placeholder: per-staff connect/disconnect is CC's ASK 2, and its three
          ops (`startCalendarSync`, `getCalendarSyncConnection`,
          `deleteCalendarSyncConnection`) answer 501 ROUTE_UNBOUND in this
          project today. A control that cannot issue a request is the same defect
          this removal is fixing. */}
      {/* ASK 2's replacement, added once the six routes were bound (2026-09-18).
          Hidden on a NEW staff member: the connection is keyed on a staff id the
          server has not seen yet, so the read would 404 on a record that does
          not exist — same rule as StaffSessions above.
          Gated on `persisted`, NOT on the id's shape (2026-09-21): a
          persisted roster member's id is itself `STAFF-<ulid>`, so an id-prefix
          test hid the control for every real staff record.
          Authorized by the INTEGRATION capabilities, not by team.write
          (2026-09-22): the connection is an integration record the server guards
          with integrations.read / integrations.write, so a team editor without
          them would see a read that 403s and a Connect button that cannot work. */}
      {!persisted || !calendarRead ? null : (
        <SvcRow label={t("staff.editorPage.googleCalendar", "Google Calendar")} hint={t("staff.editorPage.calendarHint", "Connects this person's Workspace calendar so their bookings and their own events stay in step. No sign-in prompt — the workspace's service account covers everyone.")}>
          <StaffCalendarConnection staffId={String(form.id)} staffEmail={String(form.email || "")} canWrite={calendarWrite} />
        </SvcRow>
      )}
    </div>
  );
}

/* ═══ Main editor ═════════════════════════════════════════════════════════════ */
export default function StaffEditorPage() {
  const t = useT();
  const nav = useNav();
  const store = useStaff();
  const isNew    = !!(nav.params?.new);
  const viewOnly = !!(nav.params?.view);
  const id       = nav.params?.id as string | undefined;
  /* Roster fields and Save are staffing.write: createStaff / updateStaff (CC 2026-10-17 item B).
     Sign-in sessions stay on the identity capability, as the staff contract says. */
  const canWrite = !viewOnly && can("staffing.write");
  const canManageSessions = !viewOnly && can("team.write");
  const calendarRead = can("integrations.read");
  const calendarWrite = !viewOnly && can("integrations.write");

  const { form, setForm, persistedMember, hydrated } = useStaffEditorForm({
    id, isNew, members: store.members, createEmpty: newMember,
  });
  const set = (patch: Partial<StaffMember>) => setForm((f) => ({ ...f, ...patch }));
  /* A record the server has: not the new-member form, and the id we opened
     resolved to a stored member (an unknown id falls back to newMember()). */
  const persisted = hydrated && !isNew && !!id && form.id === id;
  const [section, setSection] = useState("profile");
  /* THE PHOTO (CC 0465, staff avatars). Set or removed AT ONCE through the photo sub-resource, never on Save, and only
     on a stored member: a new member gets a photo after the record exists. */
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoErr, setPhotoErr] = useState<string | null>((nav.params?.photoErr as string | undefined) ?? null);
  const [photoNote, setPhotoNote] = useState<string | null>(null);
  const [photoUnavailable, setPhotoUnavailable] = useState(staffPhotoUnavailable());
  /* Re-read on a workspace switch or session end (CC 0467): the flag clears there, and an open editor follows. */
  useEffect(() => subscribeStaffPhoto(() => setPhotoUnavailable(staffPhotoUnavailable())), []);
  /* On a switch or sign-out the in-flight photo state is dropped HERE (CC 0470); the late answer then changes nothing. */
  useEffect(() => subscribeStaffPhotoSession(() => { setPhotoBusy(false); setPhotoErr(null); setPhotoNote(null); }), []);
  /* (generation, member) SCOPE (CC 0471). Updated on EVERY render: a photo result is applied only while the editor is
     still mounted and on the member the request was for. A→B on the same route keeps this instance mounted, so the
     member id is part of the scope, and changing member drops in-flight photo state at once. */
  const photoScope = useRef<EditorPhotoScope>({ memberId: null, mounted: true });
  photoScope.current.memberId = persisted ? form.id : null;
  useEffect(() => { photoScope.current.mounted = true; return () => { photoScope.current.mounted = false; }; }, []);
  const photoMemberSeen = useRef<string | null>(null);
  useEffect(() => {
    const now = persisted ? form.id : null;
    if (photoMemberSeen.current !== null && photoMemberSeen.current !== now) { setPhotoBusy(false); setPhotoErr(null); setPhotoNote(null); }
    photoMemberSeen.current = now;
  }, [persisted, form.id]);
  /* Upload needs the image processor; Remove never does (CC 0469), so only upload follows photoUnavailable. */
  /* Save and the photo are SERIALIZED (CC 0474 + 0476): Save waits while a photo request runs, and the photo controls
     (pick, drop, the input, Remove) wait while a Save runs. */
  const [saving, setSaving] = useState(false);
  const photoActive = !saving && photoUploadActive({ persisted, canWrite: can("staffing.write"), unavailable: photoUnavailable });
  const photoCanRemove = !saving && photoRemoveAvailable({ persisted, canWrite: can("staffing.write") });
  /* Every result goes through runEditorPhoto (CC 0471): its callbacks run only if the generation AND the member still
     match the open editor. A stale answer touches nothing; the switch / member change / unmount already reset. */
  const onPhotoFile = async (file: File) => {
    if (!photoActive) return;
    const memberId = form.id;
    const memberName = staffName(form) || t("staff.editorPage.thisStaffMember", "this staff member");
    setPhotoErr(null); setPhotoNote(null); setPhotoBusy(true);
    await runEditorPhoto(photoScope.current, memberId, () => store.setPhoto(memberId, file), {
      orphanedFailure: (text) => toast({ tone: "danger", title: t("staff.editorPage.photoNotSavedFor", "The photo for {name} wasn't saved.").replace("{name}", memberName), subtitle: text }),
      ok: (r) => {
        setPhotoBusy(false);
        set({ photo: r.url, photoUrl: r.url || null });
        /* Driven by the PUT response's processing, never by static copy. */
        if ((r.processing?.metadataRemoved ?? []).includes("location")) setPhotoNote(t("staff.editorPage.photoLocationRemoved", "Location data was removed from this photo."));
      },
      err: (text) => { setPhotoBusy(false); setPhotoErr(text); setPhotoUnavailable(staffPhotoUnavailable()); },
    });
  };
  const onPhotoRemove = async () => {
    if (!photoCanRemove) return;
    const memberId = form.id;
    const memberName = staffName(form) || t("staff.editorPage.thisStaffMember", "this staff member");
    setPhotoErr(null); setPhotoNote(null); setPhotoBusy(true);
    await runEditorPhoto(photoScope.current, memberId, () => store.removePhoto(memberId), {
      orphanedFailure: (text) => toast({ tone: "danger", title: t("staff.editorPage.photoNotRemovedFor", "The photo for {name} wasn't removed.").replace("{name}", memberName), subtitle: text }),
      ok: () => { setPhotoBusy(false); set({ photo: "", photoUrl: null }); },
      err: (text) => { setPhotoBusy(false); setPhotoErr(text); setPhotoUnavailable(staffPhotoUnavailable()); },
    });
  };
  /* Closing the tab mid-request can't be reported afterwards, so the browser asks first (CC 0474). */
  useEffect(() => {
    if (!photoBusy) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [photoBusy]);
  const photoCtl: PhotoCtl = {
    busy: photoBusy, err: photoErr, note: photoNote, unavailable: photoUnavailable, active: photoActive, canRemove: photoCanRemove, isNew: !persisted,
    onFile: (f) => { void onPhotoFile(f); }, onRemove: () => { void onPhotoRemove(); }, onRejectLink: () => setPhotoErr(STAFF_PHOTO_FROM_LINK_COPY()),
  };

  if (!isNew && id && !hydrated) {
    return (
      <Screen rail="staff">
        <div style={{ padding: 32, color: T.soft }}>
          <LoadState
            loading={store.loading || !!persistedMember}
            error={store.error}
            empty={!store.loading && !persistedMember}
            noun={t("staff.editorPage.staffNoun", "staff")}
            onRetry={store.reload}
            loadingLabel={t("common.loading", "Loading…")}
            emptyState={t("staff.editorPage.notFound", "Staff member not found.")}
            testid="staff-editor-member"
          />
        </div>
      </Screen>
    );
  }

  const SECTIONS = [
    { id: "profile",  label: t("staff.editorPage.profile", "Profile"),             icon: <Icon.user size={16} /> },
    { id: "account",  label: t("staff.editorPage.accountRole", "Account & role"),       icon: <Icon.lock size={16} /> },
    { id: "booking",  label: t("staff.editorPage.bookingAvailability", "Booking & availability"), icon: <Icon.calendar size={16} />, badge: (form.channels ?? []).length },
    { id: "schedule", label: t("staff.editorPage.locationsSchedule", "Locations & schedule"), icon: <Icon.loc size={16} />, badge: (form.locations ?? []).length },
    { id: "services", label: t("staff.editorPage.servicesGroups", "Services & groups"),    icon: <Icon.layers size={16} />, badge: (form.serviceGrants ?? []).length + ((form as { resourceIds?: string[] }).resourceIds ?? []).length },
    { id: "skills",   label: t("staff.editorPage.skillsNotifications", "Skills & notifications"), icon: <Icon.tags size={16} /> },
  ];

  const save = async () => {
    if (saving || photoBusy) return;
    setSaving(true);
    try { await saveRecord(); } finally { setSaving(false); }
  };
  const saveRecord = async () => {
    /* Save waits for the photo (CC 0474): it leaves the editor, and a photo failure must be seen here, not after. */
    if (photoBusy) return;
    if (!form.firstName.trim() && !form.lastName.trim()) { toast({ tone: "warn", title: t("staff.editorPage.addANameFirst", "Add a name first") }); setSection("profile"); return; }
    /* Contact validation is enforced HERE too, not only on blur (2026-08-07,
       Pat): a field that is never focused never blurs, and "asdf" as an email
       used to save cleanly. An address is REQUIRED for someone who signs in —
       it is the credential — and optional but well-formed for everyone else. */
    const email = (form.email ?? "").trim();
    /* An address is the credential only for a member with no linked account (CC 0374): a linked member
       signs in with their account's address, so an empty row email is fine there. */
    if (form.signsIn && !form.userId && !email) {
      toast({ tone: "warn", title: t("staff.editorPage.emailRequired", "Add an email address"), subtitle: t("staff.editorPage.emailRequiredSub", "Someone who can sign in needs the address they sign in with.") });
      setSection("profile"); return;
    }
    if (email && !isValidEmail(email)) {
      toast({ tone: "warn", title: t("staff.editorPage.emailInvalidToast", "Check the email address"), subtitle: t("staff.editorPage.emailInvalid", "Enter a valid email address — name@company.com.") });
      setSection("profile"); return;
    }
    if (!isValidPhone(form.phone ?? "")) {
      toast({ tone: "warn", title: t("staff.editorPage.phoneInvalidToast", "Check the phone number"), subtitle: t("staff.editorPage.phoneInvalid", "Enter a valid phone number — 7–15 digits, optional leading +.") });
      setSection("profile"); return;
    }
    // Commit the EFFECTIVE role (Greg T1440): a sign-in staff member always
    // resolves to the role the dropdown shows (form.role, or the default it
    // falls back to when unset) — the select can never present an empty value,
    // so a guard rejecting null was blocking saves the UI can't produce.
    /* Role only changes when it was changed (CC 0376): a member who cannot sign in keeps the role they had
       stored unless sign-in was switched off in this edit; switching it off clears it, as the toggle says. */
    const signInChanged = !!persistedMember && !!persistedMember.signsIn !== !!form.signsIn;
    const role = form.signsIn ? (form.role ?? DEFAULT_STAFF_ROLE) : (persistedMember && !signInChanged ? (persistedMember.role ?? null) : null);
    const toSave = { ...form, email, phone: normalizePhone(form.phone ?? ""), role };
    try {
      await store.upsertMember(toSave);
      toast({ tone: "success", title: isNew ? "Staff member created" : "Staff member saved" });
      nav.go("staff");
    } catch (e) {
      console.warn("staff save failed:", e);
      toast({ tone: "danger", title: t("staff.editorPage.couldnTSaveStaffMember", "Couldn't save staff member"), subtitle: t("staff.editorPage.nothingWasSavedYourEditsAreStillHereRetryInAMoment", "Nothing was saved — your edits are still here. Retry in a moment.") });
    }
  };

  return (
    <Screen rail="staff">
      <PageHeader
        title={isNew ? t("staff.list.newStaffMember", "New staff member") : (staffName(form) || t("staff.staffEditorPage.editStaffMember", "Edit staff member"))}
        subtitle={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><StaffStatusPill status={form.status} /> · {form.id}{form.signsIn ? t("staff.editorPage.text7", "· {arg1}", { arg1: form.role ?? DEFAULT_STAFF_ROLE }) : t("staff.editorPage.text6", " · cannot sign in")}</span>}
        breadcrumb={<span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("staff")}><Icon.arrow dir="left" size={11} /> {t("staff.editorPage.staff", "Staff")}</span>}
        actions={viewOnly ? <>
          {persisted ? <WelcomeEmailButton member={form} /> : null}
          {can("staffing.write") ? <Btn icon={<Icon.edit size={14} />} onClick={() => nav.go("staff.edit", { id: form.id })}>{t("common.actions.edit", "Edit")}</Btn> : null}
        </> : <>
          {persisted ? <WelcomeEmailButton member={form} /> : null}
          <Btn variant="secondary" onClick={() => nav.go("staff")}>{t("common.actions.cancel", "Cancel")}</Btn>
          <Btn icon={<Icon.check size={14} />} disabled={!canWrite || photoBusy || saving} onClick={save}>{photoBusy ? t("staff.editorPage.photoStillSaving", "Saving photo…") : isNew ? t("staff.staffEditorPage.create", "Create") : t("common.actions.save", "Save")}</Btn>
        </>}
      />
      <div style={{ flex: 1, overflow: "hidden", display: "flex", minHeight: 0 }}>
        <nav style={{ width: 232, flexShrink: 0, borderRight: `1px solid ${T.border}`, padding: "18px 14px", overflow: "auto", background: T.canvas }}>
          {SECTIONS.map((s) => { const on = section === s.id; return (
            /* ui-raw: left-hand editor section rail row — EditorSectionNav's pattern (group 8), not Button's */
            <button key={s.id} onClick={() => setSection(s.id)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 11, padding: "10px 12px", marginBottom: 4, borderRadius: 9, border: "none", background: on ? T.white : "transparent", color: on ? T.p1 : T.n2, fontWeight: on ? 700 : 500, fontSize: 13.5, cursor: "pointer", fontFamily: T.font, boxShadow: on ? T.shadowSm : "none", textAlign: "left" }}>
              <span style={{ color: on ? T.p1 : T.soft, display: "inline-flex" }}>{s.icon}</span>
              <span style={{ flex: 1 }}>{s.label}</span>
              {s.badge ? <span style={{ fontSize: 11, fontWeight: 700, color: on ? T.p1 : T.soft, background: on ? T.p4 : T.n6, borderRadius: 999, padding: "1px 7px" }}>{s.badge}</span> : null}
            </button>
          ); })}
          <div style={{ marginTop: 16, padding: "12px", borderRadius: 10, background: T.white, border: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: T.soft, marginBottom: 8 }}>{t("staff.editorPage.status", "Status")}</div>
            <SvcSelect value={form.status} onChange={(v) => set({ status: v as StaffMember["status"] })} disabled={!canWrite} options={Object.entries(STAFF_STATUSES).map(([id, v]) => ({ id, label: t(v.labelKey, v.label) }))} />
          </div>
        </nav>
        <div style={{ flex: 1, overflow: "auto", padding: "24px 28px 60px", minWidth: 0 }}>
          <div style={{ maxWidth: 760 }}>
            {section === "profile"  && <ProfileSection  form={form} set={set} canWrite={canWrite} photo={photoCtl} />}
            {section === "account"  && <AccountSection  form={form} set={set} canWrite={canWrite} canManageSessions={canManageSessions} isNew={isNew} />}
            {section === "booking"  && <BookingSection  form={form} set={set} canWrite={canWrite} isNew={isNew} />}
            {section === "schedule" && <ScheduleSection form={form} set={set} canWrite={canWrite} />}
            {section === "services" && <ServicesSection form={form} set={set} canWrite={canWrite} groups={store.groups} />}
            {section === "skills"   && <SkillsSection   form={form} set={set} canWrite={canWrite} persisted={persisted} calendarRead={calendarRead} calendarWrite={calendarWrite} />}
          </div>
        </div>
      </div>
    </Screen>
  );
}
